import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { ApiError, getWorkspaceSnapshot } from '../lib/api';
import type { WorkspaceSnapshot } from '../lib/types';

interface WorkspaceContextValue {
  data: WorkspaceSnapshot | null;
  status: 'loading' | 'ready' | 'error' | 'forbidden';
  error: string | null;
  refresh: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<WorkspaceSnapshot | null>(null);
  const [status, setStatus] = useState<WorkspaceContextValue['status']>('loading');
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setStatus('loading');
    setError(null);
    try {
      const snapshot = await getWorkspaceSnapshot();
      setData(snapshot);
      setStatus('ready');
    } catch (reason) {
      if (reason instanceof ApiError && (reason.status === 401 || reason.status === 403)) {
        setStatus('forbidden');
        setError('Ihre Sitzung besitzt keinen Zugriff auf diesen Arbeitsbereich.');
      } else {
        setStatus('error');
        setError(reason instanceof Error ? reason.message : 'Unbekannter Verbindungsfehler');
      }
    }
  };

  useEffect(() => { void refresh(); }, []);

  const value = useMemo(() => ({ data, status, error, refresh }), [data, status, error]);
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return value;
}
